import {
  Gem,
  Image as ImageIcon,
  Layers,
  Package,
  PackageOpen,
  PackagePlus,
  PenLine,
  Ruler,
  Shapes,
  SlidersHorizontal,
  Tags,
} from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import {
  getAdminV1StoresStoreIdCollections,
  getAdminV1StoresStoreIdProducts,
} from '@nucleus-commerce/api-client/admin';

import { newestProduct } from './resolvers';

const EDIT_ROUTE = '/_authed/_active/org/products/$productId';

export const productsTour = defineTour({
  id: 'catalog.products',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/products/',
  scope: 'org',
  requires: 'products.read',
  icon: Package,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'catalog.products.table' },
        { target: 'catalog.products.empty', copy: 'empty' },
      ],
    },
    {
      id: 'search',
      kind: 'action',
      target: 'catalog.products.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'filters', kind: 'explain', target: 'catalog.products.filters', optional: true },
    { id: 'status', kind: 'explain', target: 'catalog.products.status-badge', optional: true },
    {
      id: 'bulk',
      kind: 'explain',
      target: 'catalog.products.table',
      requires: 'products.write',
      optional: true,
    },
    {
      id: 'add',
      kind: 'action',
      target: 'catalog.products.add-button',
      requires: 'products.write',
    },
  ],
  related: ['catalog.product-new', 'catalog.product-editor.basics'],
});

export const productEditorBasicsTour = defineTour({
  id: 'catalog.product-editor.basics',
  version: 2,
  group: 'catalog',
  route: EDIT_ROUTE,
  scope: 'record',
  resolve: newestProduct,
  requires: 'products.read',
  icon: PenLine,
  steps: [
    { id: 'header', kind: 'explain', target: 'catalog.product-edit.header' },
    { id: 'details', kind: 'explain', target: 'catalog.product-edit.details', placement: 'right' },
    {
      id: 'attributes',
      kind: 'explain',
      target: 'catalog.product-edit.attributes',
      placement: 'right',
    },
    { id: 'seo', kind: 'explain', target: 'catalog.product-edit.seo-preview', placement: 'right' },
  ],
  related: ['catalog.product-editor.variants-media', 'catalog.product-editor.selling'],
});

export const productEditorVariantsTour = defineTour({
  id: 'catalog.product-editor.variants-media',
  version: 2,
  group: 'catalog',
  route: EDIT_ROUTE,
  scope: 'record',
  resolve: newestProduct,
  requires: 'products.read',
  icon: PenLine,
  steps: [
    {
      id: 'media',
      kind: 'explain',
      placement: 'top',
      variants: [
        { target: 'catalog.product-edit.media-empty', copy: 'empty' },
        { target: 'catalog.product-edit.media' },
      ],
    },
    { id: 'options', kind: 'explain', target: 'catalog.product-edit.options', placement: 'right' },
    {
      id: 'variants',
      kind: 'explain',
      placement: 'top',
      variants: [
        // The card is always there; the empty notice inside it wins when shown.
        { target: 'catalog.product-edit.variants-empty', copy: 'empty' },
        { target: 'catalog.product-edit.variants' },
      ],
    },
    {
      id: 'prices',
      kind: 'explain',
      target: 'catalog.product-edit.variants',
      placement: 'top',
      optional: true,
    },
    {
      id: 'configurator',
      kind: 'explain',
      target: 'catalog.product-edit.configurator',
      requires: 'configurator.read',
      optional: true,
    },
    { id: 'relations', kind: 'explain', target: 'catalog.product-edit.relations', optional: true },
  ],
  related: ['catalog.product-editor.selling', 'media.library'],
});

export const productEditorSellingTour = defineTour({
  id: 'catalog.product-editor.selling',
  version: 2,
  group: 'catalog',
  route: EDIT_ROUTE,
  scope: 'record',
  resolve: newestProduct,
  requires: 'products.read',
  icon: PenLine,
  steps: [
    { id: 'status', kind: 'explain', target: 'catalog.product-edit.status', placement: 'left' },
    {
      id: 'organisation',
      kind: 'explain',
      target: 'catalog.product-edit.organisation',
      placement: 'left',
    },
    {
      id: 'customizations',
      kind: 'explain',
      target: 'catalog.product-edit.customizations',
      placement: 'left',
    },
    {
      id: 'collections',
      kind: 'explain',
      target: 'catalog.product-edit.collections',
      placement: 'left',
      optional: true,
    },
    {
      id: 'fulfilment',
      kind: 'explain',
      target: 'catalog.product-edit.fulfilment',
      placement: 'left',
    },
    { id: 'stores', kind: 'explain', target: 'catalog.product-edit.stores', placement: 'left' },
  ],
  related: ['store-catalog.product', 'collections.list'],
});

export const storeCatalogTour = defineTour({
  id: 'store-catalog.list',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/s/$storeId/catalog/',
  scope: 'store',
  requires: 'products.read',
  icon: PackageOpen,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'store-catalog.list.table' },
        { target: 'store-catalog.list.empty', copy: 'empty' },
      ],
    },
    { id: 'filters', kind: 'explain', target: 'store-catalog.list.filters', optional: true },
    {
      id: 'bulk',
      kind: 'explain',
      target: 'store-catalog.list.table',
      requires: 'products.write',
      optional: true,
    },
    {
      id: 'add',
      kind: 'action',
      target: 'store-catalog.list.add-button',
      requires: 'products.write',
    },
    {
      id: 'dialog',
      kind: 'explain',
      target: 'store-catalog.add.dialog',
      requires: 'products.write',
      optional: true,
    },
  ],
  related: ['store-catalog.product', 'collections.list'],
});

export const productNewTour = defineTour({
  id: 'catalog.product-new',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/products/new',
  scope: 'org',
  requires: 'products.write',
  icon: PackagePlus,
  steps: [
    { id: 'details', kind: 'explain', target: 'catalog.product-new.details', placement: 'right' },
    {
      id: 'product-type',
      kind: 'explain',
      target: 'catalog.product-new.product-type',
      placement: 'left',
    },
    { id: 'options', kind: 'explain', target: 'catalog.product-new.options', placement: 'right' },
    {
      id: 'status-save',
      kind: 'explain',
      target: 'catalog.product-new.status-save',
      placement: 'left',
    },
    {
      id: 'after-save',
      kind: 'explain',
      target: 'catalog.product-new.after-save',
      placement: 'top',
    },
  ],
  related: ['catalog.product-editor.basics', 'catalog.product-editor.variants-media'],
});

export const mediaLibraryTour = defineTour({
  id: 'media.library',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/media',
  scope: 'org',
  requires: 'products.read',
  icon: ImageIcon,
  steps: [
    {
      id: 'grid',
      kind: 'explain',
      placement: 'top',
      variants: [
        { target: 'media.library.grid' },
        { target: 'media.library.empty', copy: 'empty' },
      ],
    },
    {
      id: 'upload',
      kind: 'explain',
      target: 'media.library.upload-button',
      requires: 'media.write',
    },
    {
      id: 'search',
      kind: 'action',
      target: 'media.library.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'filters', kind: 'explain', target: 'media.library.filters' },
    { id: 'missing-alt', kind: 'explain', target: 'media.library.missing-alt' },
  ],
  related: ['catalog.product-editor.variants-media'],
});

export const attributesTour = defineTour({
  id: 'dictionary.attributes',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/catalog-setup/attributes/',
  scope: 'org',
  requires: 'products.read',
  icon: Tags,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'dictionary.attributes.table' },
        { target: 'dictionary.attributes.empty', copy: 'empty' },
      ],
    },
    { id: 'flags', kind: 'explain', target: 'dictionary.attributes.flags', optional: true },
    {
      id: 'new',
      kind: 'action',
      target: 'dictionary.attributes.new-button',
      requires: 'attributes.write',
    },
  ],
  related: ['dictionary.product-types'],
});

export const customizationsTour = defineTour({
  id: 'dictionary.customizations',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/catalog-setup/customizations',
  scope: 'org',
  requires: 'products.read',
  icon: SlidersHorizontal,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'dictionary.customizations.table' },
        { target: 'dictionary.customizations.empty', copy: 'empty' },
      ],
    },
    {
      id: 'price-delta',
      kind: 'explain',
      target: 'dictionary.customizations.price-delta',
      optional: true,
    },
    {
      id: 'new',
      kind: 'action',
      target: 'dictionary.customizations.new-button',
      requires: 'attributes.write',
    },
  ],
  related: ['catalog.product-editor.selling', 'dictionary.size-sets'],
});

export const productTypesTour = defineTour({
  id: 'dictionary.product-types',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/catalog-setup/product-types/',
  scope: 'org',
  requires: 'products.read',
  icon: Shapes,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'dictionary.product-types.table' },
        { target: 'dictionary.product-types.empty', copy: 'empty' },
      ],
    },
    {
      id: 'row-actions',
      kind: 'explain',
      target: 'dictionary.product-types.row-actions',
      requires: 'attributes.write',
      optional: true,
    },
    {
      id: 'new',
      kind: 'action',
      target: 'dictionary.product-types.new-button',
      requires: 'attributes.write',
    },
  ],
  related: ['dictionary.attributes', 'catalog.product-new'],
});

export const sizeSetsTour = defineTour({
  id: 'dictionary.size-sets',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/catalog-setup/size-sets/',
  scope: 'org',
  requires: 'products.read',
  icon: Ruler,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'dictionary.size-sets.table' },
        { target: 'dictionary.size-sets.empty', copy: 'empty' },
      ],
    },
    {
      id: 'new',
      kind: 'action',
      target: 'dictionary.size-sets.new-button',
      requires: 'attributes.write',
    },
  ],
  related: ['dictionary.customizations'],
});

export const stonePricingTour = defineTour({
  id: 'dictionary.stone-pricing',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/org/stone-pricing',
  scope: 'org',
  requires: 'configurator.read',
  icon: Gem,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'bands',
      kind: 'explain',
      variants: [
        { target: 'dictionary.stone-pricing.stone-type-card' },
        { target: 'dictionary.stone-pricing.empty', copy: 'empty' },
      ],
    },
    {
      id: 'store-column',
      kind: 'explain',
      target: 'dictionary.stone-pricing.store-column',
      optional: true,
    },
    {
      id: 'new',
      kind: 'action',
      target: 'dictionary.stone-pricing.new-button',
      requires: 'configurator.write',
    },
  ],
  related: ['catalog.product-editor.variants-media'],
});

/** The first store product of the list; its page shows every section. */
const firstStoreProduct: TourResolver = {
  key: 'first-store-product',
  thing: 'things.storeProduct',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdProducts({ storeId }, { limit: 1 });
    const row = page.items[0];
    return row ? { storeId, storeProductId: row.id } : null;
  },
};

export const storeProductTour = defineTour({
  id: 'store-catalog.product',
  version: 2,
  group: 'catalog',
  route: '/_authed/_active/s/$storeId/catalog/$storeProductId',
  scope: 'record',
  resolve: firstStoreProduct,
  requires: 'products.read',
  icon: PackageOpen,
  steps: [
    {
      id: 'status-save',
      kind: 'explain',
      target: 'store-catalog.product.status-save',
      placement: 'left',
    },
    {
      id: 'schedule',
      kind: 'explain',
      target: 'store-catalog.product.schedule',
      placement: 'left',
    },
    {
      id: 'collections',
      kind: 'explain',
      target: 'store-catalog.product.collections',
      placement: 'left',
      optional: true,
    },
    {
      id: 'overrides',
      kind: 'explain',
      target: 'store-catalog.product.overrides',
      placement: 'right',
    },
    { id: 'pricing', kind: 'explain', target: 'store-catalog.product.pricing', placement: 'top' },
    {
      id: 'market-prices',
      kind: 'explain',
      target: 'store-catalog.product.market-prices',
      optional: true,
    },
    {
      id: 'master-link',
      kind: 'explain',
      target: 'store-catalog.product.master-link',
      placement: 'bottom',
    },
  ],
  related: ['collections.list', 'settings.markets'],
});

export const collectionsListTour = defineTour({
  id: 'collections.list',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/s/$storeId/collections/',
  scope: 'store',
  requires: 'products.read',
  icon: Layers,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'collections.list.table' },
        { target: 'collections.list.empty', copy: 'empty' },
      ],
    },
    { id: 'kind', kind: 'explain', target: 'collections.list.kind-badge', optional: true },
    {
      id: 'add',
      kind: 'action',
      target: 'collections.list.add-button',
      requires: 'collections.write',
    },
  ],
  related: ['collections.new', 'collections.editor'],
});

export const collectionNewTour = defineTour({
  id: 'collections.new',
  version: 1,
  group: 'catalog',
  route: '/_authed/_active/s/$storeId/collections/new',
  scope: 'store',
  requires: 'collections.write',
  icon: Layers,
  steps: [
    { id: 'kind', kind: 'explain', target: 'collections.new.kind', placement: 'right' },
    { id: 'details', kind: 'explain', target: 'collections.edit.details', placement: 'right' },
    {
      id: 'rules',
      kind: 'explain',
      target: 'collections.edit.rules',
      placement: 'right',
      optional: true,
      waitMs: 1000,
    },
    {
      id: 'status-save',
      kind: 'explain',
      target: 'collections.edit.status-save',
      placement: 'left',
    },
  ],
  related: ['collections.editor'],
});

/** The collection someone touched last. */
const newestCollection: TourResolver = {
  key: 'newest-collection',
  thing: 'things.collection',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdCollections(
      { storeId },
      { limit: 1, sort: '-updatedAt' },
    );
    const row = page.items[0];
    return row ? { storeId, collectionId: row.id } : null;
  },
};

export const collectionEditorTour = defineTour({
  id: 'collections.editor',
  version: 2,
  group: 'catalog',
  route: '/_authed/_active/s/$storeId/collections/$collectionId',
  scope: 'record',
  resolve: newestCollection,
  requires: 'products.read',
  icon: Layers,
  steps: [
    {
      id: 'status-save',
      kind: 'explain',
      target: 'collections.edit.status-save',
      placement: 'left',
    },
    {
      id: 'products',
      kind: 'explain',
      placement: 'right',
      variants: [
        { target: 'collections.edit.manual-products' },
        { target: 'collections.edit.rules', copy: 'rules' },
      ],
    },
    {
      id: 'rules-preview',
      kind: 'explain',
      target: 'collections.edit.rules-preview',
      placement: 'right',
      optional: true,
      waitMs: 1000,
    },
    {
      id: 'sort-order',
      kind: 'explain',
      target: 'collections.edit.sort-order',
      placement: 'left',
    },
    {
      id: 'placement',
      kind: 'explain',
      target: 'collections.edit.placement',
      placement: 'left',
    },
    { id: 'image', kind: 'explain', target: 'collections.edit.image', placement: 'right' },
    { id: 'seo', kind: 'explain', target: 'collections.edit.seo', placement: 'right' },
  ],
  related: ['content.menus'],
});
